<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { getters as auth, logout } from '../services/auth.service';
import NotificationBell from '../components/notification-bell.vue';
import ToastHost from '../components/toast-host.vue';

// vite.config.js sets publicDir to ./src/assets, so this file is served at
// the root path. Vite rejects JS-side imports of publicDir assets.
const lockupWhite = '/brand/lockup-white.svg';

const route = useRoute();
const router = useRouter();

const navItems = computed(() =>
    router
        .getRoutes()
        .filter((r) => r.meta?.sidebar)
        // Hide role-restricted items (e.g. Tenant is admin-only).
        .filter(
            (r) =>
                !r.meta.sidebar.roles ||
                r.meta.sidebar.roles.some((role) => auth.hasRole(role))
        )
        .sort(
            (a, b) =>
                (a.meta.sidebar.order ?? 99) - (b.meta.sidebar.order ?? 99)
        )
        .map((r) => ({
            name: r.name,
            label: r.meta.sidebar.label,
            icon: r.meta.icon || 'icon-nav',
            // Pinned to the bottom of the nav, just above the profile.
            bottom: r.meta.sidebar.bottom || false,
        }))
);

const quickAddOpen = ref(false);
const quickAddRoot = ref(null);

const profileMenuOpen = ref(false);
const profileMenuRoot = ref(null);

const quickAddItems = computed(() => {
    const items = [];
    const stationsRoute = router.hasRoute('station-register');

    if (stationsRoute) {
        items.push({
            label: 'Add a Station',
            icon: 'icon-energy',
            onClick: () => {
                quickAddOpen.value = false;
                router.push({ name: 'station-register' });
            },
        });
    }
    items.push({
        label: 'Add a Location',
        icon: 'icon-location',
        onClick: () => {
            quickAddOpen.value = false;
            router.push({ name: 'locations', query: { create: '1' } });
        },
    });

    return items;
});

function handleQuickAddOutside(event) {
    if (!quickAddOpen.value) {return;}

    if (quickAddRoot.value && quickAddRoot.value.contains(event.target)) {return;}
    quickAddOpen.value = false;
}

watch(quickAddOpen, (open) => {
    if (open) {
        // Defer so the same click that opened the menu doesn't close it.
        setTimeout(() => document.addEventListener('mousedown', handleQuickAddOutside), 0);
    } else {
        document.removeEventListener('mousedown', handleQuickAddOutside);
    }
});

function handleProfileOutside(event) {
    if (!profileMenuOpen.value) {return;}

    if (profileMenuRoot.value && profileMenuRoot.value.contains(event.target)) {return;}
    profileMenuOpen.value = false;
}

watch(profileMenuOpen, (open) => {
    if (open) {
        setTimeout(() => document.addEventListener('mousedown', handleProfileOutside), 0);
    } else {
        document.removeEventListener('mousedown', handleProfileOutside);
    }
});

onBeforeUnmount(() => {
    document.removeEventListener('mousedown', handleQuickAddOutside);
    document.removeEventListener('mousedown', handleProfileOutside);
});

function openAccount() {
    profileMenuOpen.value = false;
    router.push({ name: 'account' });
}

function isActive(name) {
    if (route.name === name) {return true;}

    if (name === 'stations' && (route.name === 'station-detail' || route.name === 'station-register')) {
        return true;
    }

    if (name === 'locations' && route.name === 'location-detail') {return true;}

    return false;
}
</script>

<template>
    <div class="hive-shell">
        <aside class="hive-sidebar">
            <header class="hive-sidebar__header">
                <img
                    :src="lockupWhite"
                    alt="Hive"
                    class="hive-sidebar__logo"
                />
                <notification-bell class="hive-sidebar__bell-wrapper" />
            </header>

            <div ref="quickAddRoot" class="hive-sidebar__quick-add">
                <button
                    type="button"
                    class="hive-sidebar__new-btn"
                    :aria-expanded="quickAddOpen"
                    @click="quickAddOpen = !quickAddOpen"
                >
                    <j-icon icon-id="icon-add" class="hive-sidebar__new-icon" />
                    <span>New</span>
                </button>
                <div v-if="quickAddOpen" class="hive-sidebar__quick-menu">
                    <button
                        v-for="item in quickAddItems"
                        :key="item.label"
                        type="button"
                        class="hive-sidebar__quick-item"
                        @click="item.onClick"
                    >
                        <j-icon :icon-id="item.icon" class="hive-sidebar__quick-item-icon" />
                        <span>{{ item.label }}</span>
                    </button>
                </div>
            </div>

            <nav class="hive-sidebar__nav" aria-label="Main">
                <router-link
                    v-for="item in navItems"
                    :key="item.name"
                    v-slot="{ navigate }"
                    :to="{ name: item.name }"
                    custom
                >
                    <button
                        type="button"
                        :class="[
                            'hive-nav-item',
                            item.bottom ? 'hive-nav-item--bottom' : '',
                            isActive(item.name) ? 'hive-nav-item--active' : '',
                        ]"
                        @click="navigate"
                    >
                        <j-icon :icon-id="item.icon" class="hive-nav-item__icon" />
                        <span class="hive-nav-item__label">{{ item.label }}</span>
                    </button>
                </router-link>
            </nav>

            <footer ref="profileMenuRoot" class="hive-sidebar__footer">
                <div v-if="profileMenuOpen" class="hive-sidebar__profile-menu">
                    <div class="hive-sidebar__profile-menu-header">
                        <span class="hive-sidebar__profile-menu-name">
                            {{ auth.profile?.displayName || 'User' }}
                        </span>
                        <span class="hive-sidebar__profile-menu-email">
                            {{ auth.profile?.email || '' }}
                        </span>
                    </div>
                    <button
                        type="button"
                        class="hive-sidebar__profile-item"
                        @click="openAccount"
                    >
                        <j-icon icon-id="icon-user" class="hive-sidebar__profile-item-icon" />
                        <span>Personal account</span>
                    </button>
                    <button
                        type="button"
                        class="hive-sidebar__profile-item"
                        @click="logout"
                    >
                        <j-icon icon-id="icon-log-out" class="hive-sidebar__profile-item-icon" />
                        <span>Log out</span>
                    </button>
                </div>

                <button
                    type="button"
                    class="hive-sidebar__profile"
                    :aria-expanded="profileMenuOpen"
                    aria-label="Open account menu"
                    @click="profileMenuOpen = !profileMenuOpen"
                >
                    <span class="hive-sidebar__avatar">
                        <j-icon icon-id="icon-user" />
                    </span>
                    <span class="hive-sidebar__profile-meta">
                        <span class="hive-sidebar__profile-name">
                            {{ auth.profile?.displayName || auth.profile?.email || 'User' }}
                        </span>
                        <span class="hive-sidebar__profile-org">
                            {{ auth.profile?.orgId || '' }}
                        </span>
                    </span>
                </button>
            </footer>
        </aside>

        <main class="hive-content">
            <slot></slot>
        </main>

        <toast-host />
    </div>
</template>

<style scoped>
.hive-shell {
    display: flex;
    width: 100%;
    height: 100vh;
    background: var(--neutral-100);
}

.hive-sidebar {
    width: 240px;
    flex-shrink: 0;
    background: var(--brand-primary);
    color: white;
    display: flex;
    flex-direction: column;
    padding: 16px;
    gap: 16px;
    overflow-y: auto;
}

.hive-sidebar__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.hive-sidebar__logo {
    height: 28px;
    width: auto;
}

.hive-sidebar__bell-wrapper {
    color: white;
}

.hive-sidebar__quick-add {
    position: relative;
}

.hive-sidebar__new-btn {
    width: 100%;
    background: rgba(255, 255, 255, 0.08);
    color: white;
    border: 1px solid rgba(255, 255, 255, 0.18);
    padding: 10px 14px;
    border-radius: 8px;
    cursor: pointer;
    text-align: left;
    font: inherit;
    display: flex;
    align-items: center;
    gap: 8px;
}

.hive-sidebar__new-btn:hover {
    background: rgba(255, 255, 255, 0.14);
}

.hive-sidebar__new-icon {
    width: 18px;
    height: 18px;
    fill: currentColor;
    flex-shrink: 0;
}

.hive-sidebar__quick-menu {
    position: absolute;
    left: 0;
    right: 0;
    margin-top: 4px;
    background: white;
    color: var(--neutral-900);
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18);
    z-index: 10;
}

.hive-sidebar__quick-item {
    background: transparent;
    border: 0;
    width: 100%;
    padding: 10px 14px;
    text-align: left;
    cursor: pointer;
    font: inherit;
    color: inherit;
    display: flex;
    align-items: center;
    gap: 10px;
}

.hive-sidebar__quick-item:hover {
    background: var(--neutral-100);
}

.hive-sidebar__quick-item-icon {
    width: 18px;
    height: 18px;
    fill: currentColor;
    flex-shrink: 0;
}

.hive-sidebar__nav {
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex: 1;
}

.hive-nav-item {
    background: transparent;
    border: 0;
    color: rgba(255, 255, 255, 0.85);
    text-align: left;
    padding: 10px 12px;
    border-radius: 8px;
    cursor: pointer;
    font: inherit;
    width: 100%;
    display: flex;
    align-items: center;
    gap: 10px;
}

.hive-nav-item__icon {
    width: 18px;
    height: 18px;
    fill: currentColor;
    flex-shrink: 0;
}

.hive-nav-item:hover {
    background: rgba(255, 255, 255, 0.06);
    color: white;
}

.hive-nav-item--active {
    background: rgba(255, 255, 255, 0.14);
    color: white;
    font-weight: 600;
}

/* Pinned to the foot of the nav: absorbs the free space above so it sits
   just above the profile in the footer. */
.hive-nav-item--bottom {
    margin-top: auto;
}

.hive-sidebar__footer {
    margin-top: auto;
    position: relative;
}

.hive-sidebar__profile {
    width: 100%;
    background: rgba(255, 255, 255, 0.06);
    color: white;
    padding: 8px 10px;
    border: 0;
    border-radius: 8px;
    cursor: pointer;
    font: inherit;
    display: flex;
    align-items: center;
    gap: 10px;
    text-align: left;
}

.hive-sidebar__profile:hover {
    background: rgba(255, 255, 255, 0.12);
}

.hive-sidebar__profile-menu {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 100%;
    margin-bottom: 6px;
    background: white;
    color: var(--neutral-900);
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.22);
    z-index: 10;
}

.hive-sidebar__profile-menu-header {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 10px 14px;
    border-bottom: 1px solid var(--neutral-100);
}

.hive-sidebar__profile-menu-name {
    font-size: 0.8125rem;
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.hive-sidebar__profile-menu-email {
    font-size: 0.6875rem;
    color: var(--neutral-600);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.hive-sidebar__profile-item {
    background: transparent;
    border: 0;
    width: 100%;
    padding: 10px 14px;
    text-align: left;
    cursor: pointer;
    font: inherit;
    color: inherit;
    display: flex;
    align-items: center;
    gap: 10px;
}

.hive-sidebar__profile-item:hover {
    background: var(--neutral-100);
}

.hive-sidebar__profile-item-icon {
    width: 18px;
    height: 18px;
    fill: currentColor;
    flex-shrink: 0;
}

.hive-sidebar__avatar {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: var(--brand-accent);
    color: var(--brand-accent-text);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.hive-sidebar__avatar :deep(svg) {
    width: 18px;
    height: 18px;
    fill: currentColor;
}

.hive-sidebar__profile-meta {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-width: 0;
}

.hive-sidebar__profile-name {
    font-size: 0.8125rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.hive-sidebar__profile-org {
    font-size: 0.6875rem;
    opacity: 0.7;
}

.hive-content {
    flex: 1;
    overflow-y: auto;
    background: var(--neutral-50);
}
</style>
